import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors } from '@angular/forms';
import * as moment from 'moment';
import { combineLatest, Subject } from 'rxjs';
import { distinctUntilChanged, startWith, takeUntil } from 'rxjs/operators';

import { TimeInputComponent } from '../time-input/time-input.component';

/*
**************************************
* TimeRangeInputComponent Help Guide *
**************************************


Setting up TimeRangeInputComponent is easy. You just need to pass a FormControl
to it. Default values and basic error validation will be automatically taken
care of:
```
public timeControl = new FormControl();
```

```
<sp-time-range-input [control]="timeControl"></sp-time-range-input>
```

The control will track the range via an object `{start: string, end: string}`.
The strings are the times in 24 clock format. So 4:38 PM start time, 11:22 PM 
end time would be `{start: '16:38', end: '23:22'}`. You can represent a blank
field with `''` and can set you own initial values in the control.

For example, if you want the start to be 2 PM and the end to be blank, you 
would do the following:
```
public timeControl = new FormControl({start: '14:00', end: ''});
```

You can also disable the form or set values in the parent. To disable, add
`timeControl.disable()`. To set value:
`timeControl.setValue({start: '06:01', end: '07:44'})`


Finally, you can also add you own validators. When you set an error, you can
have a message for the `start` input, the `end` input, or `both`. You do this 
by having your validator return an error formatted like
`{ start: 'Start input is bad'}`. When you set a custom error, it takes priority
over the default errors.

The following validator sets a custom message while erroring both fields when
`start` is after `end` and it's 11:11 AM:

```
public timeControl = new FormControl(null, validateTimeRange);

private validateTimeRange(control: FormControl): { [key: string]: string } | null {
	const { start, end } = control.value || {};
	if (start && start >= end && start === '11:11') {
		return { both: 'Whacky doo, a custom error for you!' };
	}
	return null;
}

Hint: if you want to change the "Range must end after it starts" message, add a
custom validator!

```
*/

export type TimeRangeInputType = { start: string; end: string };

//removed onpush temporarily because it was causing issues with change detection for period list component
// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
	// changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'sp-time-range-input',
	templateUrl: './time-range-input.component.html',
	styleUrls: ['./time-range-input.component.scss'],
})
export class TimeRangeInputComponent implements OnInit, AfterViewInit {
	@Input() control!: FormControl<TimeRangeInputType | null>;
	@Input() additionalDuration = 10; // Default duration is 10 minutes

	@Output() inputChanged: EventEmitter<TimeRangeInputType> = new EventEmitter<TimeRangeInputType>();
	@Output() focusChanged: EventEmitter<{ startInputFocused: boolean; endInputFocused: boolean }> = new EventEmitter<{
		startInputFocused: boolean;
		endInputFocused: boolean;
	}>();

	@ViewChild('startInput') startInput!: TimeInputComponent;
	@ViewChild('endInput') endInput!: TimeInputComponent;

	startInputFocused = false;
	endInputFocused = false;
	errorMsg = '';
	initStartTime = '';
	initEndTime = '';
	initError = 1;
	private destroy$ = new Subject<void>();
	private prevStart: string | null = null;
	private prevEnd: string | null = null;

	ngOnInit() {
		const value = this.control?.value;

		// Default next half time point from now and 10 minutes from start time
		const startTime: string =
			value?.start !== undefined && value?.start !== null
				? value.start
				: new Date(Math.ceil(new Date().getTime() / (30 * 60 * 1000)) * (30 * 60 * 1000)).toLocaleTimeString('en-GB', {
						hour: '2-digit',
						minute: '2-digit',
						hour12: false,
				  });

		const endTime: string =
			value?.end !== undefined && value?.end !== null
				? value.end
				: new Date(new Date('1970-01-01T' + startTime + ':00Z').getTime() + this.additionalDuration * 60000).toISOString().substring(11, 16);
		this.control.setValue({
			start: startTime,
			end: endTime,
		});
		this.initStartTime = startTime;
		this.initEndTime = endTime;

		// Hide initial error if initial values are null or empty
		if (!startTime || !endTime) {
			this.initError = 0;
		}

		this.control.setValidators([this.defaultTimeValidators, ...(this.control.validator ? [this.control.validator] : [])]);

		// Handle parent using setValues
		this.control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((time) => {
			if (time) {
				this.prevStart = time.start;
				this.prevEnd = time.end;
				this.startInput.setInput(time.start);
				this.endInput.setInput(time.end);
			}
		});
	}

	ngAfterViewInit() {
		// Track focus state
		this.startInput.anyInputFocusedChanged.pipe(takeUntil(this.destroy$)).subscribe((focused: boolean) => {
			this.startInputFocused = focused;
			this.focusChanged.emit({ startInputFocused: focused, endInputFocused: this.endInputFocused });
		});
		this.endInput.anyInputFocusedChanged.pipe(takeUntil(this.destroy$)).subscribe((focused: boolean) => {
			this.endInputFocused = focused;
			this.focusChanged.emit({ startInputFocused: this.startInputFocused, endInputFocused: focused });
		});

		// Move cursor from start period to end hour
		this.startInput.switchToEndInput.pipe(takeUntil(this.destroy$)).subscribe((isArrowKey) => this.endInput.focusOnHour(isArrowKey));
		// Move cursor from end hour to start period
		this.endInput.switchToStartInput.pipe(takeUntil(this.destroy$)).subscribe((isArrowKey) => this.startInput.focusOnPeriod(isArrowKey));

		// Emit values to parent and build error
		combineLatest([
			this.startInput.timeFormValueChanges.pipe(startWith(this.initStartTime)),
			this.endInput.timeFormValueChanges.pipe(startWith(this.initEndTime)),
		])
			.pipe(
				distinctUntilChanged((prev, curr) => prev[0] === curr[0] && prev[1] === curr[1]),
				takeUntil(this.destroy$)
			)
			.subscribe(([start, end]) => {
				if (start == null || end == null) {
					return;
				}

				// If start time changes, end time should move forward the same amount
				if (this.prevStart && this.prevEnd && this.prevStart !== start) {
					end = this.calcNewEndTime(this.prevStart, this.prevEnd, start);
				}
				this.control.setValue({ start: start, end: end });
				if (this.initError > 1) {
					this.inputChanged.emit({ start: start, end: end });
				}

				// Hide error display if default values are bad
				this.initError++;
			});
	}

	defaultTimeValidators = (control: AbstractControl): ValidationErrors | null => {
		const { start, end } = (control.value as { start: string; end: string }) || {};
		const errors: Record<string, string> = {};

		if (!start) {
			errors.start = 'Invalid time';
		}
		if (!end) {
			errors.end = 'Invalid time';
		}

		if (!start && !end) {
			errors.both = 'Invalid times';
		}

		// No input provided
		if (start === '') {
			errors.start = 'Please select a start time';
		} else if (end === '') {
			errors.end = 'Please select an end time';
		}
		if (start && start >= end) {
			errors.start = 'Pick a start time earlier than the end time';
		}

		return Object.keys(errors).length ? errors : null;
	};

	private calcNewEndTime(prevStart: string, prevEnd: string, currentStart: string): string {
		// Convert time strings to Date objects
		const prevStartDate = new Date(`1970-01-01T${prevStart}:00`);
		const prevEndDate = new Date(`1970-01-01T${prevEnd}:00`);
		const currentStartDate = new Date(`1970-01-01T${currentStart}:00`);

		// Calculate the difference in minutes
		const duration = (prevEndDate.getTime() - prevStartDate.getTime()) / (1000 * 60);

		// Add the duration to currentStart
		const newEndDate = new Date(currentStartDate.getTime() + duration * 60000);

		// Format the result back into "hh:mm" format
		const hours = newEndDate.getHours().toString().padStart(2, '0');
		const minutes = newEndDate.getMinutes().toString().padStart(2, '0');

		return `${hours}:${minutes}`;
	}
}

/**
 * Helper functions to convert between timestamp start and end times and TimeRangeInputType
 */
export function getDateFromTimeRange(
	date: moment.Moment,
	timeRange: TimeRangeInputType,
	tz: string
): { startTime: moment.Moment; endTime: moment.Moment } {
	const startTime = moment.tz(date.format('YYYY-MM-DD') + ' ' + timeRange.start, tz);
	const endTime = moment.tz(date.format('YYYY-MM-DD') + ' ' + timeRange.end, tz);
	return { startTime, endTime };
}

export function getTimeRangeFromStartEndDates(startDate: Date | undefined, endDate: Date | undefined): TimeRangeInputType {
	const startTime = startDate ? moment(startDate).format('HH:mm') : '';
	const endTime = endDate ? moment(endDate).format('HH:mm') : '';
	return { start: startTime, end: endTime };
}
